/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useState } from 'react';
import { cn } from '@/shared/ui/utils/tailwind';
import type { FacebookSettings } from './facebook-settings.helpers';
import { daysUntilExpiry } from './facebook-settings.helpers';
import { DisconnectFooter, FbIcon, IgIcon, Toggle, NetworkRow, tokenHealthTone, tokenHealthLabel } from './FacebookSettingsParts';
import { PostDescriptionTemplateModal } from './PostDescriptionTemplateModal';
import { LeadAdDefaultsModal, type LeadAdConnectionConfig } from './LeadAdDefaultsModal';
import type { LeadAdContentValues } from './LeadAdContentFields';
import { FirstPostPrompt } from './FirstPostPrompt';
import type { DescriptionFieldKey } from './description-template-fields';
import { FF_APP_FACEBOOK_MARKETPLACE, FF_APP_FACEBOOK_ADS } from '@/shared/utils/feature-flag';
import { Anchor, Button, List, ListItem, StatusBadge, Text, Title, UnstyledButton } from '@/shared/ui';
import { MetaAdsTermsLinks } from '@/features/property-management/components/ad-dialogs/MetaAdsTermsLinks';
import { BlockMid, SettingsBlock } from '../blocks';

/** Props for the Facebook/Instagram settings section content body. */
export interface ContentProps {
  agencyId: string;
  settings: FacebookSettings;
  isFbEnabled: boolean;
  isIgEnabled: boolean;
  showDisconnectConfirm: boolean;
  showIgNativeDisconnectConfirm: boolean;
  igNativeDisconnecting: boolean;
  onTogglePortal: (portal: 'facebook' | 'instagram') => void;
  onUpdateSettings: (updates: Partial<FacebookSettings>) => Promise<void>;
  onDisconnect: () => void;
  onShowDisconnect: () => void;
  onCancelDisconnect: () => void;
  onChangePage: () => void;
  onDisconnectIgNative: () => void;
  onShowIgNativeDisconnect: () => void;
  onCancelIgNativeDisconnect: () => void;
  /** Disconnects an Instagram-via-Page connection -- in practice the same as disconnecting Facebook, since the Page is what carries the Instagram link. */
  onDisconnectIgViaPage: () => void;
  showIgViaPageDisconnectConfirm: boolean;
  onShowIgViaPageDisconnect: () => void;
  onCancelIgViaPageDisconnect: () => void;
  /** Starts the combined Facebook (+ Instagram-via-Page, when available) OAuth flow. */
  onConnectFacebook: () => void;
  /** Starts the Facebook flow while explicitly keeping Instagram publishing off. Only offered from the nothing-connected chooser. */
  onConnectFacebookOnly: () => void;
  /** Starts the native Instagram-only OAuth flow. */
  onConnectInstagram: () => void;
  instagramConnectAvailable: boolean;
  instagramConnecting: boolean;
  t: (key: string, opts?: Record<string, string | number>) => string;
}

/**
 * The "Redes Sociais" settings blocks. Two shapes only:
 * - Nothing connected: one block with the onboarding chooser (benefits,
 *   prerequisites, and the three explicit connect modes).
 * - Anything connected: a block with a Facebook {@link NetworkRow} and an
 *   Instagram {@link NetworkRow}, always in that same shape regardless of
 *   which of the three connection modes applies -- a disconnected network
 *   shows a Connect button in its row instead of a toggle/badge, but the row
 *   itself never changes shape -- followed by one titled block per default
 *   (post description, Lead Ads), the same way the Portais tab stacks them.
 *
 * @param props - See {@link ContentProps}.
 * @returns The rendered settings blocks.
 */
export const Content: React.FC<ContentProps> = (props) => {
  const { settings } = props;
  const igConnected = Boolean(settings.ig_account_id) || settings.ig_native_connected;
  if (!settings.fb_connected && !igConnected) {
    return (
      <SettingsBlock testId="facebook-settings-block">
        <BlockMid>
          <Disconnected {...props} />
        </BlockMid>
      </SettingsBlock>
    );
  }
  return <ConnectedBody {...props} />;
};

/**
 * Groups a NetworkRow's disconnect line with an optional leading notice
 * (e.g. the Instagram-via-Page dependency warning) and an optional trailing
 * extra section (e.g. the connect-Facebook-for-ads hint) inside the same footer
 * slot, so this content renders inside that platform's own bordered card
 * instead of floating in a separate, unstyled section below it.
 */
const NetworkFooterGroup: React.FC<{ disconnect: React.ReactNode; notice?: React.ReactNode; extra?: React.ReactNode }> = ({ disconnect, notice, extra }) => (
  <div className="flex flex-col gap-(--space-3)">
    {notice}
    {disconnect}
    {extra && (
      <div className="pt-(--space-3) border-t border-(--border-subtle)">
        {extra}
      </div>
    )}
  </div>
);

/**
 * Proactive warning shown on the Instagram row whenever that Instagram
 * account is only connected because it's linked to the Facebook Page (as
 * opposed to the separate Native Instagram Business Login) -- so the agency
 * knows, before they ever try to disconnect, that the two are tied
 * together. The reactive version of this same fact already exists at
 * disconnect-confirm time (`facebook.desconectarInstagramViaPaginaConfirmacao`);
 * this is the always-visible counterpart.
 */
const IgPageDependencyNotice: React.FC<{ t: (key: string) => string }> = ({ t }) => (
  <div className="flex items-start gap-(--space-2) text-xs text-(--text-tertiary)" data-testid="ig-page-dependency-notice">
    <LinkIcon />
    <Text component="span" className="leading-relaxed">{t('facebook.instagramVinculadoAviso')}</Text>
  </div>
);

const ConnectedBody: React.FC<ContentProps> = ({
  agencyId,
  settings,
  isFbEnabled,
  isIgEnabled,
  showDisconnectConfirm,
  showIgNativeDisconnectConfirm,
  igNativeDisconnecting,
  onTogglePortal,
  onUpdateSettings,
  onDisconnect,
  onShowDisconnect,
  onCancelDisconnect,
  onChangePage,
  onDisconnectIgNative,
  onShowIgNativeDisconnect,
  onCancelIgNativeDisconnect,
  onDisconnectIgViaPage,
  showIgViaPageDisconnectConfirm,
  onShowIgViaPageDisconnect,
  onCancelIgViaPageDisconnect,
  onConnectFacebook,
  onConnectInstagram,
  instagramConnectAvailable,
  instagramConnecting,
  t,
}) => {
  const [showPostDescriptionModal, setShowPostDescriptionModal] = useState(false);
  const [showLeadAdModal, setShowLeadAdModal] = useState(false);

  const igConnectedViaPage = Boolean(settings.ig_account_id) && !settings.ig_native_connected;
  const igConnectedNative = settings.ig_native_connected;
  const igConnected = igConnectedViaPage || igConnectedNative;

  const fbTone = tokenHealthTone(daysUntilExpiry(settings.fb_token_expires_at ?? undefined));
  const igDays = daysUntilExpiry(settings.ig_native_token_expires_at);
  const igTone = tokenHealthTone(igDays);
  const igHealthLabel = tokenHealthLabel(igTone, t);

  // Ads always bill through the Facebook ad account, so Instagram Lead Ad
  // eligibility depends on Facebook's own payment method, not Instagram's.
  const fbLeadAdEligible = FF_APP_FACEBOOK_ADS && settings.fb_connected && settings.fb_has_payment_method;
  const igLeadAdEligible = FF_APP_FACEBOOK_ADS && igConnected && settings.fb_has_payment_method;

  // Whether disconnecting Facebook could leave behind an active Lead Ad
  // campaign it should pause -- an ad account is a prerequisite for ever
  // having created one, regardless of the card's current status.
  const fbMayHaveActiveAds = FF_APP_FACEBOOK_ADS && !!settings.fb_ad_account_id;

  const leadAdConnections: LeadAdConnectionConfig[] = [];
  if (fbLeadAdEligible) {
    leadAdConnections.push({
      key: 'facebook',
      icon: <FbIcon size={16} color="#fff" />,
      iconBg: '#1877F2',
      label: settings.fb_page_name ? `${t('facebook.facebook')} · ${settings.fb_page_name}` : t('facebook.facebook'),
      budgetCents: settings.fb_lead_ad_budget,
      duration: settings.fb_lead_ad_duration,
      titlePrefix: 'defaultLeadAd',
      testIdPrefix: 'facebook',
      onSave: async (budgetCents, duration) => { await onUpdateSettings({ fb_lead_ad_budget: budgetCents, fb_lead_ad_duration: duration }); },
    });
  }
  if (igLeadAdEligible) {
    const igUsername = igConnectedNative ? settings.ig_native_username : settings.ig_account_name;
    leadAdConnections.push({
      key: 'instagram',
      icon: <IgIcon size={16} color="#fff" />,
      iconBg: '#E4405F',
      label: igUsername ? `Instagram · @${igUsername}` : 'Instagram',
      budgetCents: settings.ig_lead_ad_budget,
      duration: settings.ig_lead_ad_duration,
      titlePrefix: 'defaultLeadAdInstagram',
      testIdPrefix: 'instagram',
      onSave: async (budgetCents, duration) => { await onUpdateSettings({ ig_lead_ad_budget: budgetCents, ig_lead_ad_duration: duration }); },
    });
  }

  const leadAdContent = {
    values: {
      headline: settings.lead_ad_headline,
      closing: settings.lead_ad_message_closing,
      radiusKm: settings.lead_ad_radius_km,
      formQuestion: settings.lead_ad_form_question,
      thankYouMessage: settings.lead_ad_thank_you_message,
      websiteUrl: settings.lead_ad_website_url,
      privacyUrl: settings.lead_ad_privacy_url,
    } satisfies LeadAdContentValues,
    onSave: async (v: LeadAdContentValues) => {
      await onUpdateSettings({
        lead_ad_headline: v.headline.trim(),
        lead_ad_message_closing: v.closing.trim(),
        lead_ad_radius_km: v.radiusKm,
        lead_ad_form_question: v.formQuestion.trim(),
        lead_ad_thank_you_message: v.thankYouMessage.trim(),
        lead_ad_website_url: v.websiteUrl.trim(),
        lead_ad_privacy_url: v.privacyUrl.trim(),
      });
    },
  };

  // Shared between both Instagram NetworkRow branches (via-Page / native) --
  // only one of the two branches ever renders, so this is computed once.
  const igAdsExtra = FF_APP_FACEBOOK_ADS && !settings.fb_connected ? (
    <Text component="span" className="text-sm text-(--text-secondary) leading-relaxed">
      {t('facebook.conecteFacebookParaAds')}
    </Text>
  ) : null;

  return (
    <div className="flex flex-col gap-(--space-4)">
      <SettingsBlock testId="facebook-settings-block">
        <BlockMid>
          <div className="flex flex-col gap-(--space-4)">
            {FF_APP_FACEBOOK_ADS && settings.fb_connected && !settings.fb_ad_account_id && (
              <div
                data-testid="no-ad-account-banner"
                className="flex items-start gap-(--space-3) rounded-(--radius-md) border border-[var(--status-warning-border)] bg-[var(--status-warning-bg)] px-(--space-4) py-(--space-3)"
              >
                <WarningIcon />
                <div className="flex-1 min-w-0">
                  <Text className="m-0 text-sm font-semibold text-[var(--status-warning-text)]">{t('facebook.leadAdSection.semAdAccountTitulo')}</Text>
                  <Text className="m-0 mt-(--space-0-5) text-sm text-[var(--status-warning-text)] leading-relaxed">{t('facebook.leadAdSection.semAdAccountDescricao')}</Text>
                  <Anchor
                    href="https://business.facebook.com/business_locations"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-(--space-1-5) inline-block text-xs font-semibold text-[var(--status-warning-text)] underline hover:text-[var(--status-warning-text)]"
                  >
                    {t('facebook.leadAdSection.semAdAccountLink')}
                  </Anchor>
                </div>
              </div>
            )}

            {FF_APP_FACEBOOK_ADS && settings.fb_connected && !!settings.fb_ad_account_id && !settings.fb_has_payment_method && (
              <div
                data-testid="no-payment-method-banner"
                className="flex items-start gap-(--space-3) rounded-(--radius-md) border border-[var(--status-warning-border)] bg-[var(--status-warning-bg)] px-(--space-4) py-(--space-3)"
              >
                <WarningIcon />
                <div className="flex-1 min-w-0">
                  <Text className="m-0 text-sm font-semibold text-[var(--status-warning-text)]">{t('facebook.semCartaoTitulo')}</Text>
                  <Text className="m-0 mt-(--space-0-5) text-sm text-[var(--status-warning-text)] leading-relaxed">{t('facebook.semCartaoDescricao')}</Text>
                  <Anchor
                    href="https://business.facebook.com/billing_hub/payment_settings/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-(--space-1-5) inline-block text-xs font-semibold text-[var(--status-warning-text)] underline hover:text-[var(--status-warning-text)]"
                  >
                    {t('facebook.semCartaoLink')}
                  </Anchor>
                </div>
              </div>
            )}

            {settings.fb_connected ? (
                <NetworkRow
                  testId="network-row-facebook"
                  icon={<FbIcon size={20} color="#fff" />}
                  iconBg="#1877F2"
                  title={t('facebook.facebook')}
                  identity={settings.fb_page_name ? t('facebook.paginaConectada', { pageName: settings.fb_page_name }) : undefined}
                  trailing={<Toggle checked={isFbEnabled} testId="toggle-facebook" onClick={() => onTogglePortal('facebook')} />}
                  meta={<StatusBadge tone={fbTone}>{tokenHealthLabel(fbTone, t)}</StatusBadge>}
                  footer={
                    <NetworkFooterGroup
                      disconnect={
                        <DisconnectFooter
                          confirming={showDisconnectConfirm}
                          onTrigger={onShowDisconnect}
                          onCancel={onCancelDisconnect}
                          onConfirm={onDisconnect}
                          t={t}
                          testIdPrefix="disconnect"
                          extraWarning={fbMayHaveActiveAds ? t('facebook.desconectarAdsAviso') : undefined}
                          leading={
                            <UnstyledButton data-testid="change-page-btn" onClick={onChangePage} className="text-xs text-(--text-tertiary) underline cursor-pointer bg-transparent border-none p-0">
                              {t('facebook.alterarPagina')}
                            </UnstyledButton>
                          }
                        />
                      }
                    />
                  }
                />
              ) : (
                <NetworkRow
                  testId="network-row-facebook"
                  icon={<FbIcon size={18} />}
                  title={t('facebook.facebook')}
                  hint={t('facebook.dicaFacebook')}
                  trailing={
                    <Button variant="outlined" size="xs" data-testid="connect-facebook-row-btn" onClick={onConnectFacebook}>
                      {t('facebook.conectarFacebook')}
                    </Button>
                  }
                />
              )}

              {isFbEnabled && FF_APP_FACEBOOK_MARKETPLACE && settings.fb_connected && (
                <div className="flex items-start gap-(--space-3) pl-(--space-8)">
                  <Toggle checked={settings.fb_marketplace_enabled} size={16} testId="toggle-marketplace" onClick={() => onUpdateSettings({ fb_marketplace_enabled: !settings.fb_marketplace_enabled })} />
                  <div className="flex-1">
                    <Text component="span" className="text-sm font-medium text-(--text-primary)">{t('facebook.marketplace')}</Text>
                    <div className="text-sm text-(--text-secondary) mt-(--space-1) leading-relaxed">{t('facebook.dicaMarketplace')}</div>
                  </div>
                </div>
              )}

              {igConnectedViaPage && (
                <NetworkRow
                  testId="network-row-instagram"
                  icon={<IgIcon size={20} color="#fff" />}
                  iconBg="#E4405F"
                  title="Instagram"
                  identity={t('instagramNative.contaConectada', { username: settings.ig_account_name ?? '' })}
                  trailing={<Toggle checked={isIgEnabled} testId="toggle-instagram" onClick={() => onTogglePortal('instagram')} />}
                  meta={<StatusBadge tone={fbTone}>{tokenHealthLabel(fbTone, t)}</StatusBadge>}
                  footer={
                    <NetworkFooterGroup
                      disconnect={
                        <DisconnectFooter
                          confirming={showIgViaPageDisconnectConfirm}
                          onTrigger={onShowIgViaPageDisconnect}
                          onCancel={onCancelIgViaPageDisconnect}
                          onConfirm={onDisconnectIgViaPage}
                          t={t}
                          testIdPrefix="disconnect-instagram-via-page"
                          confirmMessage={t('facebook.desconectarInstagramViaPaginaConfirmacao')}
                          extraWarning={fbMayHaveActiveAds ? t('facebook.desconectarAdsAviso') : undefined}
                          leading={
                            <UnstyledButton data-testid="change-page-instagram-btn" onClick={onChangePage} className="text-xs text-(--text-tertiary) underline cursor-pointer bg-transparent border-none p-0">
                              {t('facebook.alterarPagina')}
                            </UnstyledButton>
                          }
                        />
                      }
                      notice={<IgPageDependencyNotice t={t} />}
                      extra={igAdsExtra}
                    />
                  }
                />
              )}

              {igConnectedNative && (
                <NetworkRow
                  testId="network-row-instagram"
                  icon={<IgIcon size={20} color="#fff" />}
                  iconBg="#E4405F"
                  title="Instagram"
                  identity={t('instagramNative.contaConectada', { username: settings.ig_native_username ?? '' })}
                  trailing={<Toggle checked={isIgEnabled} testId="toggle-instagram" onClick={() => onTogglePortal('instagram')} />}
                  meta={<StatusBadge tone={igTone}>{igHealthLabel}</StatusBadge>}
                  footer={
                    <NetworkFooterGroup
                      disconnect={
                        <DisconnectFooter
                          confirming={showIgNativeDisconnectConfirm}
                          busy={igNativeDisconnecting}
                          onTrigger={onShowIgNativeDisconnect}
                          onCancel={onCancelIgNativeDisconnect}
                          onConfirm={onDisconnectIgNative}
                          t={t}
                          testIdPrefix="disconnect-instagram-native"
                          leading={
                            <UnstyledButton data-testid="change-instagram-account-btn" onClick={onConnectInstagram} className="text-xs text-(--text-tertiary) underline cursor-pointer bg-transparent border-none p-0">
                              {t('instagramNative.trocarConta')}
                            </UnstyledButton>
                          }
                        />
                      }
                      extra={igAdsExtra}
                    />
                  }
                />
              )}

              {!igConnected && instagramConnectAvailable && (
                <NetworkRow
                  testId="network-row-instagram"
                  icon={<IgIcon size={18} />}
                  title="Instagram"
                  hint={t('facebook.dicaInstagram')}
                  trailing={
                    <Button variant="outlined" size="xs" data-testid="connect-instagram-row-btn" disabled={instagramConnecting} onClick={onConnectInstagram}>
                      {t('facebook.conectarInstagramSomente')}
                    </Button>
                  }
                />
              )}

              {!igConnected && !instagramConnectAvailable && (
                <NetworkRow
                  testId="network-row-instagram"
                  icon={<IgIcon size={18} />}
                  title="Instagram"
                  hint={t('facebook.instagramAtivaAoConectarFacebook')}
                  dimmed
                />
              )}

              <FirstPostPrompt agencyId={agencyId} t={t} />
          </div>
        </BlockMid>
      </SettingsBlock>

      <SettingsBlock
        testId="post-description-block"
        title={t('facebook.postDescription.sectionTitle')}
        caption={t('facebook.postDescription.modalDescription')}
        action={{
          label: t('facebook.postDescription.editButton'),
          onClick: () => setShowPostDescriptionModal(true),
          testId: 'edit-post-description-btn',
        }}
      />

      <PostDescriptionTemplateModal
        open={showPostDescriptionModal}
        onClose={() => setShowPostDescriptionModal(false)}
        fields={settings.post_description_fields as DescriptionFieldKey[] | null | undefined}
        onSave={(fields) => onUpdateSettings({ post_description_fields: fields })}
        t={t}
      />

      {leadAdConnections.length > 0 && (
        <SettingsBlock
          testId="lead-ads-block"
          title={t('facebook.leadAdSection.sectionTitle')}
          caption={t('facebook.leadAdSection.sectionDescription')}
          action={{
            label: t('facebook.leadAdSection.configureButton'),
            onClick: () => setShowLeadAdModal(true),
            testId: 'configure-lead-ads-btn',
          }}
        />
      )}

      <LeadAdDefaultsModal
        open={showLeadAdModal}
        onClose={() => setShowLeadAdModal(false)}
        connections={leadAdConnections}
        content={leadAdContent}
        t={t}
      />
    </div>
  );
};

const Disconnected: React.FC<ContentProps> = ({
  onConnectFacebook,
  onConnectFacebookOnly,
  onConnectInstagram,
  instagramConnectAvailable,
  instagramConnecting,
  t,
}) => (
  <div>
    <div className="flex justify-center -space-x-3 mb-(--space-4)">
      <div className="w-12 h-12 rounded-full bg-[var(--surface-primary)] border-2 border-(--surface-primary) flex items-center justify-center shadow-sm">
        <FbIcon size={28} />
      </div>
      <div className="w-12 h-12 rounded-full bg-[var(--surface-primary)] border-2 border-(--surface-primary) flex items-center justify-center shadow-sm">
        <IgIcon size={28} />
      </div>
    </div>

    <Title order={2} size="lg" className="m-0 text-center font-semibold text-(--text-primary)">{t('facebook.tituloIntegracao')}</Title>
    <Text className="m-0 mt-(--space-1) text-center text-sm text-(--text-secondary) leading-relaxed">{t('facebook.descricaoConexao')}</Text>

    <div className="grid grid-cols-1 md:grid-cols-2 gap-(--space-4) mt-(--space-6) mb-(--space-6)">
      <div className="rounded-(--radius-lg) bg-(--surface-secondary) p-(--space-4)">
        <Text className="m-0 text-xs font-semibold text-(--text-tertiary) uppercase tracking-wide mb-(--space-3)">{t('facebook.beneficiosTitulo')}</Text>
        <List size="sm" spacing="sm" className="m-0 pl-0 list-none flex flex-col gap-(--space-2-5)">
          {[
            t('facebook.beneficio1'),
            t('facebook.beneficio2'),
            ...(FF_APP_FACEBOOK_ADS ? [t('facebook.beneficio3')] : []),
            t('facebook.beneficio4'),
          ].map((txt) => (
            <ListItem
              key={txt}
              className="items-center text-sm text-(--text-primary)"
              icon={<svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>}
            >
              {txt}
            </ListItem>
          ))}
        </List>
      </div>
      <div className="rounded-(--radius-lg) bg-[var(--status-warning-bg)]/60 border border-[var(--status-warning)]/50 p-(--space-4)">
        <Text className="m-0 text-xs font-semibold text-[var(--status-warning-text)] uppercase tracking-wide mb-(--space-3)">{t('facebook.prerequisitosTitulo')}</Text>
        <List size="sm" spacing="sm" className="m-0 pl-0 list-none flex flex-col gap-(--space-2-5)">
          {[t('facebook.prerequisito1'), t('facebook.prerequisito2')].map((txt) => (
            <ListItem
              key={txt}
              className="text-sm text-[var(--status-warning-text)]"
              icon={<Text component="span" className="block w-1.5 h-1.5 rounded-full bg-[var(--status-warning)]" />}
            >
              {txt}
            </ListItem>
          ))}
        </List>
      </div>
    </div>

    {FF_APP_FACEBOOK_ADS && (
      <div className="flex flex-col gap-(--space-1-5) mb-(--space-4)" data-testid="ads-authorization-notice">
        <Text className="m-0 text-xs leading-relaxed text-(--text-secondary)">{t('facebook.adsAuthorizationNotice')}</Text>
        <MetaAdsTermsLinks />
      </div>
    )}

    <div className="flex flex-col gap-(--space-2-5)">
      <UnstyledButton
        onClick={onConnectFacebook}
        data-testid="connect-facebook-and-instagram-btn"
        className={cn(
          'w-full py-(--space-3) text-sm font-semibold rounded-(--radius-md)',
          'bg-(--button-primary-bg) hover:bg-(--button-primary-hover-bg) active:bg-(--button-primary-active-bg) text-(--button-primary-text)',
          'flex items-center justify-center gap-(--space-2)',
          'cursor-pointer border-none transition-colors',
        )}
      >
        <FbIcon size={16} color="#fff" />
        <IgIcon size={16} color="#fff" />
        {t('facebook.conectarFacebookEInstagram')}
      </UnstyledButton>

      <UnstyledButton
        onClick={onConnectFacebookOnly}
        data-testid="connect-facebook-only-btn"
        className={cn(
          'w-full py-(--space-3) text-sm font-semibold rounded-(--radius-md)',
          'bg-[var(--brand-facebook)] hover:bg-[var(--brand-facebook-hover)] text-white',
          'flex items-center justify-center gap-(--space-2)',
          'cursor-pointer border-none transition-colors',
        )}
      >
        <FbIcon size={16} color="#fff" />
        {t('facebook.conectarFacebookSomente')}
      </UnstyledButton>

      {instagramConnectAvailable && (
        <>
          <UnstyledButton
            onClick={onConnectInstagram}
            disabled={instagramConnecting}
            data-testid="connect-instagram-only-btn"
            className={cn(
              'w-full py-(--space-3) text-sm font-semibold rounded-(--radius-md)',
              'bg-[var(--brand-instagram)] hover:bg-[var(--brand-instagram-hover)] text-white',
              'flex items-center justify-center gap-(--space-2)',
              'cursor-pointer border-none transition-colors',
              instagramConnecting && 'opacity-70 cursor-not-allowed',
            )}
          >
            <IgIcon size={16} color="#fff" />
            {t('facebook.conectarInstagramSomente')}
          </UnstyledButton>
          <Text className="m-0 text-center text-xs text-(--text-tertiary)">{t('facebook.instagramSomenteNota')}</Text>
        </>
      )}
    </div>

    <Text className="m-0 text-center text-xs text-(--text-tertiary) mt-(--space-3)">{t('facebook.tempoEstimado')}</Text>
    <Text className="m-0 text-center text-xs text-(--text-secondary) mt-(--space-4)">
      {t('facebook.ajudaPagina')}{' '}
      <Anchor href="https://www.facebook.com/business/help/473994396650734" target="_blank" rel="noopener noreferrer" className="font-medium text-[var(--seed-blue)] hover:underline">
        {t('facebook.ajudaPaginaLink')}
      </Anchor>
    </Text>
  </div>
);

const WarningIcon: React.FC = () => (
  <svg
    className="mt-(--space-0-5) shrink-0 text-[var(--status-warning)]"
    width={16}
    height={16}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
    <line x1="12" y1="9" x2="12" y2="13" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

const LinkIcon: React.FC = () => (
  <svg
    className="mt-px shrink-0 text-(--text-tertiary)"
    width={14}
    height={14}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71" />
    <path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71" />
  </svg>
);
